import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, take } from 'rxjs';
import type Stripe from 'stripe';
import { AppConfigService } from '../providers/config.service';
import { Promotion } from '../types/promotion.type';
import { Proration } from '../types/proration';
import { PricingService } from './pricing.service';
import { PromotionService } from './promotion.service';

@Injectable({
  providedIn: 'root',
})
export class ProrationService {
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private pricingService = inject(PricingService);
  private promotionService = inject(PromotionService);

  public getProration(price: Stripe.Price, promo?: Promotion): Observable<Proration> {
    const prices: { id: string; quantity?: number }[] = [{ id: price.id }];

    return forkJoin([
      this.http
        .patch<Stripe.Invoice>(`${this.config.apiEndpoint}/billing/subscriptions/prorate`, {
          prices,
          promotion_code: promo?.code,
        })
        .pipe(take(1)),
      this.pricingService.getPrices('main').pipe(take(1)),
    ]).pipe(
      map(([resp, mainPrices]) => {
        if (!resp) return undefined;

        const allLines = resp.lines.data;

        allLines.sort((a, b) => {
          const aIsMain = mainPrices.find(p => p.id === a.price.id);
          const bIsMain = mainPrices.find(p => p.id === b.price.id);
          if (aIsMain && !bIsMain) return -1;
          if (bIsMain && !aIsMain) return 1;
          if (a.type === 'subscription') return -1;
          if (b.type === 'subscription') return 1;
          return 0;
        });

        const products = [...new Set(allLines.map(line => line.price.product))];

        const lines = [];
        products.forEach(product => {
          allLines.filter(line => line.price.product === product).forEach(line => lines.push(line));
        });

        const subTotal = lines.reduce((total, line) => total + line.amount, 0);
        const usedBalance = -((resp.starting_balance || 0) - (resp.ending_balance || 0));
        const totalDiscount = lines.reduce((total, line) => total + line.discount_amounts[0]?.amount || 0, 0);

        let prorationDiscount: { start: number; end: number; promo: Promotion };

        for (const line of lines) {
          if (!line.discount_amounts[0]?.amount) continue; // we don't want to show a discount that has no impact on the invoice
          const discount = line.discount_amounts[0]?.discount;
          if (discount) {
            prorationDiscount = {
              start: discount.start * 1000,
              end: discount.end * 1000,
              promo: this.promotionService.convertPromo(discount),
            };
            break;
          }
        }

        return {
          ...resp,
          lines,
          subTotal,
          usedBalance,
          totalDiscount,
          discount: prorationDiscount,
        };
      })
    );
  }
}
